import { AlertTriangle, Check, Clock, Delete, Loader2, LogOut, Monitor, Moon, Sun } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, unlockSession } from '../api/client';
import type { Me } from '../api/schemas';
import { useFarbschema } from '../app/farbschema';
import { FameSignet } from '../ui/FameSignet';
import { Wortmarke } from '../ui/Wortmarke';

/**
 * Der Sperrbildschirm — Auftrag `A-sitzungssperre` (2026-09-10), Meldung des
 * Auftraggebers: «Die Login-Session soll länger halten; nach 60 Minuten
 * Inaktivität nicht vollständig ausloggen, sondern ein Lock-Screen, in dem man
 * nur das 2FA-Token erneut eingibt.»
 *
 * Gebaut nach `claude-design-concept/lockscreen/` — Karte über einem
 * unscharfen Platzhaltergerüst, Signet, Name und Adresse, «Gesperrt seit» und
 * Gerät, sechs Ziffernfelder mit wanderndem Cursor, Ziffernblock und
 * Tastatur, automatische Prüfung bei der sechsten Ziffer, Zustände Standard /
 * Prüfung / Fehler / Erfolg, Hell und Dunkel, «Abmelden».
 *
 * ---------------------------------------------------------------------------
 * WAS HINTER DEM SCHLEIER STEHT — und was ausdrücklich nicht
 *
 * **Das Gerüst im Hintergrund ist ein Platzhalter und trägt kein einziges
 * echtes Datum.** Das Konzept verlangt das wörtlich («keine echten Inhalte»),
 * und es ist die einzige haltbare Fassung: Ein unscharf gerechnetes Bild der
 * wirklichen Seite wäre bei jeder Schriftgrösse anders gut lesbar, und
 * «unscharf genug» ist keine Zusage, die sich prüfen lässt. Graue Balken
 * dagegen sagen nichts, in jeder Auflösung.
 *
 * **Die laufende Anwendung bleibt derweil montiert, aber unsichtbar**, und
 * darüber entscheidet `app/App.tsx`, nicht diese Datei. Das ist der Kern des
 * Versprechens im Fuss dieser Karte — «Deine Eingaben und offenen Entwürfe
 * bleiben erhalten»: Es wird nichts weggeschrieben und nichts
 * wiederhergestellt; der Zustand jeder offenen Maske bleibt schlicht, wo er
 * ist. Die Begründung samt ihrer Grenze steht in `App.tsx`.
 *
 * ---------------------------------------------------------------------------
 * DAS EINGABEFELD — ein echtes `<input>` unter sechs gemalten Kästchen
 *
 * Sechs eigene Felder wären sechs eigene Eingaben, und ein Passwortverwalter
 * füllte in das erste die ganze Zahl. Hier liegt deshalb **ein** durchsichtiges
 * `<input>` über der Kästchenreihe; die Kästchen zeigen nur, was darin steht.
 * Es trägt dieselben vier Merkmale wie das Feld der Anmeldemaske
 * (`TotpForm.tsx`): `autocomplete="one-time-code"`, `inputmode="numeric"`,
 * `pattern="[0-9]*"` und `name`/`id` = `totp` — Fall `E-1129`.
 *
 * `aria-hidden` an der Kästchenreihe: Sie ist ein Bild des Feldes und sagt
 * nichts, was das Feld nicht selbst sagt.
 */

/** Sechs Stellen. Die Zahl steht hier einmal und nicht in vier Schleifen. */
const STELLEN = 6;

/**
 * Wie viele Fehlversuche die Sitzung überlebt.
 *
 * **Diese Zahl steht doppelt — hier und in `auth.service.ts`
 * (`MAX_UNLOCK_ATTEMPTS`) — und das ist hingenommen, nicht übersehen.** Der
 * Server schickt den Zählerstand bewusst NICHT mit (dieselbe Zurückhaltung
 * wie bei der Drossel: eine Restanzeige verriete einem Angreifer, wie dicht
 * er an der Grenze steht). Der Bildschirm zählt seine eigenen Fehlschläge
 * mit — was er ohnehin kann, weil er sie selbst ausgelöst hat — und
 * braucht die Grenze, um «Noch 3 Versuche» sagen zu können. Läuft sie
 * auseinander, ist die Folge eine falsche Zahl im Hinweistext und **nie**
 * eine falsche Entscheidung: Über das Ende der Sitzung entscheidet
 * ausschliesslich der Server.
 */
const MAX_VERSUCHE = 5;

/** Der Zustand des Formulars. Vier Lagen, wie im Konzept. */
type Lage =
  | { readonly art: 'standard' }
  | { readonly art: 'pruefung' }
  | { readonly art: 'fehler'; readonly text: string; readonly verbleibend: number }
  | { readonly art: 'erfolg' };

export function Sperrbildschirm(props: {
  readonly user: Me;
  /** Nach erfolgreichem Entsperren — `App.tsx` holt `/auth/me` neu. */
  readonly onEntsperrt: () => void;
  /** Wenn die Sitzung dabei zu Ende gegangen ist (fünf Fehlversuche, Ablauf). */
  readonly onAbgemeldet: () => void;
  /** Der Knopf «Abmelden». */
  readonly onSignOut: () => void;
}): ReactNode {
  const [code, setCode] = useState('');
  const [lage, setLage] = useState<Lage>({ art: 'standard' });
  const [versuche, setVersuche] = useState(0);
  const feld = useRef<HTMLInputElement | null>(null);
  const farbschema = useFarbschema();
  const dunkel = farbschema.schema === 'dunkel';

  const laeuft = lage.art === 'pruefung' || lage.art === 'erfolg';

  const entsperren = useCallback(
    async (eingabe: string): Promise<void> => {
      setLage({ art: 'pruefung' });
      try {
        await unlockSession(eingabe);
        setLage({ art: 'erfolg' });
        props.onEntsperrt();
      } catch (fehler) {
        // Eine 401 heisst hier: Die Sitzung gibt es nicht mehr — fünf
        // Fehlversuche, ein zurückgesetztes Gerät oder die abgelaufene
        // absolute Lebensdauer. Der Server hat das Cookie in derselben
        // Antwort gelöscht; der Sperrbildschirm hat nichts mehr aufzuschliessen
        // und macht der Anmeldemaske Platz.
        if (fehler instanceof ApiError && fehler.isAuthRequired) {
          props.onAbgemeldet();
          return;
        }
        const naechster = versuche + 1;
        setVersuche(naechster);
        setCode('');
        setLage({
          art: 'fehler',
          text:
            fehler instanceof ApiError
              ? fehler.message
              : 'Der Server ist nicht erreichbar. Bitte erneut versuchen.',
          verbleibend: Math.max(0, MAX_VERSUCHE - naechster),
        });
        feld.current?.focus();
      }
    },
    [props, versuche],
  );

  /**
   * Die automatische Prüfung bei der sechsten Ziffer.
   *
   * **Als Effekt und nicht in der Eingabebehandlung**, weil die Ziffern aus
   * zwei Quellen kommen — Tastatur und Ziffernblock — und beide sonst
   * dieselbe Bedingung tragen müssten. Die Wächterzeile `lage.art` verhindert,
   * dass eine noch laufende Prüfung ein zweites Mal anläuft.
   */
  useEffect(() => {
    if (code.length !== STELLEN) return;
    if (lage.art === 'pruefung' || lage.art === 'erfolg') return;
    void entsperren(code);
  }, [code, lage.art, entsperren]);

  /** Beim Erscheinen in das Feld: Wer tippt, soll nicht erst klicken müssen. */
  useEffect(() => {
    feld.current?.focus();
  }, []);

  const ziffer = (wert: string): void => {
    if (laeuft) return;
    setCode((bisher) => (bisher + wert).slice(0, STELLEN));
  };

  const loeschen = (): void => {
    if (laeuft) return;
    setCode((bisher) => bisher.slice(0, -1));
  };

  const stellen = Array.from({ length: STELLEN }, (_, i) => code[i] ?? '');

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <Platzhaltergeruest />

      {/*
        Der Schleier. `backdrop-blur` zeichnet das Gerüst weich; die Fläche
        darüber nimmt ihm den Rest an Kontrast. Beides zusammen, weil
        `backdrop-blur` allein auf schmalen Balken kaum sichtbar ist.
      */}
      <div aria-hidden="true" className="absolute inset-0 bg-background/70 backdrop-blur-sm" />

      <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-8">
        <section
          aria-labelledby="sperrbildschirm-titel"
          className="w-full max-w-[440px] overflow-hidden rounded-[20px] border border-strong bg-card shadow-elev"
        >
          {/* Die Kopflinie trägt den Zustand als Farbe: Marke, Gefahr, Erfolg. */}
          <div
            aria-hidden="true"
            className={`h-[3px] ${
              lage.art === 'fehler' ? 'bg-danger' : lage.art === 'erfolg' ? 'bg-ok' : 'bg-primary'
            }`}
          />

          <div className="p-6 sm:p-7">
            <div className="mb-5 flex items-center gap-3 border-b border-border pb-4">
              <FameSignet className="size-[26px] shrink-0" />
              <Wortmarke className="text-[15px] text-foreground" />
            </div>

            <div className="mb-5 flex items-center gap-3.5">
              <span
                aria-hidden="true"
                className="grid size-[54px] shrink-0 place-items-center rounded-full bg-brand-line text-lg font-bold tracking-[-0.02em] text-primary"
              >
                {initialen(props.user.displayName)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15.5px] font-semibold tracking-[-0.015em]">
                  {props.user.displayName}
                </div>
                <div className="truncate text-[12.5px] text-text3">{props.user.email}</div>
              </div>
              {/*
                Derselbe Umschalter wie auf der Anmeldemaske und aus demselben
                Grund: Der Sperrbildschirm füllt den ganzen Schirm, und wer im
                Dunkeln arbeitet, bekäme ihn sonst ungefragt hell.
              */}
              <button
                type="button"
                title={dunkel ? 'Helle Darstellung' : 'Dunkle Darstellung'}
                onClick={farbschema.umschalten}
                className="grid size-[34px] shrink-0 place-items-center rounded-[9px] border border-border text-muted-foreground hover:bg-secondary"
              >
                {dunkel ? (
                  <Sun aria-hidden="true" className="size-[18px]" />
                ) : (
                  <Moon aria-hidden="true" className="size-[18px]" />
                )}
              </button>
            </div>

            <h1
              id="sperrbildschirm-titel"
              className="mb-2 text-[24px] font-semibold leading-[1.15] tracking-[-0.028em]"
            >
              Sitzung gesperrt
            </h1>
            <p className="mb-5 text-pretty text-sm text-muted-foreground">
              Nach 60 Minuten ohne Aktivität sperrt FameAccounting den Bildschirm. Deine Sitzung
              läuft weiter — gib zum Entsperren nur den Code aus deiner Authenticator-App ein.
            </p>

            <div className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-border bg-secondary px-3.5 py-3">
              <Kontext
                sinnbild={<Clock aria-hidden="true" className="size-[15px]" />}
                label="Gesperrt seit"
                wert={uhrzeit(props.user.gesperrtSeit)}
              />
              <Kontext
                sinnbild={<Monitor aria-hidden="true" className="size-[15px]" />}
                label="Gerät"
                wert={props.user.geraet ?? 'Unbekannt'}
              />
            </div>

            {/*
              `role="alert"` und nicht bloss ein roter Kasten: Die
              Fehlermeldung erscheint, ohne dass der Fokus sie berührt — wer
              mit einer Sprachausgabe arbeitet, erführe sonst nur, dass sein
              Feld plötzlich leer ist.
            */}
            {lage.art === 'fehler' ? (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2.5 rounded-[11px] border border-danger bg-danger-soft px-3.5 py-3"
              >
                <AlertTriangle aria-hidden="true" className="mt-px size-[18px] shrink-0 text-danger" />
                <div>
                  <div className="mb-0.5 text-[13px] font-semibold text-danger">
                    Code nicht gültig
                  </div>
                  <div className="text-[12.5px] text-muted-foreground">
                    {lage.text} Prüfe die Uhrzeit deines Geräts und gib den aktuellen Code ein.{' '}
                    {lage.verbleibend === 1
                      ? 'Noch 1 Versuch bis zur vollständigen Abmeldung.'
                      : `Noch ${String(lage.verbleibend)} Versuche bis zur vollständigen Abmeldung.`}
                  </div>
                </div>
              </div>
            ) : null}

            <label htmlFor="totp" className="mb-2.5 block text-xs font-medium text-muted-foreground">
              6-stelliger Code aus deiner Authenticator-App
            </label>

            <div className="relative mb-4">
              <div aria-hidden="true" className="flex gap-2">
                {stellen.map((zeichen, index) => (
                  <span
                    key={index}
                    className={`relative grid h-[52px] flex-1 place-items-center rounded-[11px] border font-zahl text-xl font-medium ${
                      lage.art === 'fehler'
                        ? 'border-danger bg-danger-soft'
                        : lage.art === 'erfolg'
                          ? 'border-ok bg-ok-soft'
                          : index === code.length
                            ? 'border-ring bg-card ring-2 ring-ring/40'
                            : 'border-strong bg-secondary'
                    }`}
                  >
                    {zeichen}
                    {index === code.length && !laeuft ? (
                      <span className="absolute h-6 w-px animate-pulse bg-primary" />
                    ) : null}
                  </span>
                ))}
              </div>
              {/*
                Das echte Feld: durchsichtig, aber vollständig da — es liegt
                über der Kästchenreihe, nimmt den Fokus, den Klick und die
                Eingabe des Passwortverwalters entgegen. `opacity-0` und nicht
                `sr-only`, weil ein aus dem Bild geschobenes Feld manche
                Verwalter dazu bringt, es zu übergehen.
              */}
              <input
                ref={feld}
                id="totp"
                name="totp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={STELLEN}
                disabled={laeuft}
                value={code}
                onChange={(ereignis) => {
                  setCode(ereignis.target.value.replace(/\D/g, '').slice(0, STELLEN));
                }}
                className="absolute inset-0 h-full w-full cursor-default rounded-[11px] bg-transparent text-transparent caret-transparent opacity-0 outline-none"
              />
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((wert) => (
                <Taste key={wert} label={wert} onClick={() => { ziffer(wert); }} disabled={laeuft} />
              ))}
              <span aria-hidden="true" />
              <Taste label="0" onClick={() => { ziffer('0'); }} disabled={laeuft} />
              <Taste
                label="Löschen"
                onClick={loeschen}
                disabled={laeuft}
                inhalt={<Delete aria-hidden="true" className="size-[18px]" />}
              />
            </div>

            {/*
              Der Knopf ist der **zweite** Weg und nicht der einzige: Bei der
              sechsten Ziffer prüft der Bildschirm von selbst. Er bleibt
              trotzdem stehen, weil ein Formular ohne sichtbaren Abschluss für
              jeden, der es zum ersten Mal sieht, unfertig aussieht.
            */}
            <button
              type="button"
              disabled={laeuft || code.length !== STELLEN}
              onClick={() => {
                void entsperren(code);
              }}
              className="flex h-[46px] w-full items-center justify-center gap-2.5 rounded-[11px] bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {lage.art === 'pruefung' ? (
                <Loader2 aria-hidden="true" className="size-[15px] animate-spin" />
              ) : null}
              {lage.art === 'erfolg' ? <Check aria-hidden="true" className="size-[15px]" /> : null}
              {lage.art === 'pruefung'
                ? 'Wird geprüft …'
                : lage.art === 'erfolg'
                  ? 'Entsperrt'
                  : 'Entsperren'}
            </button>

            <div className="mt-4 flex items-center justify-center">
              <button
                type="button"
                onClick={props.onSignOut}
                className="flex items-center gap-2 text-[12.5px] text-text3 hover:text-danger"
              >
                <LogOut aria-hidden="true" className="size-[15px]" />
                Abmelden
              </button>
            </div>
          </div>
        </section>

        <p className="mt-5 max-w-[440px] text-center text-xs text-text3">
          Deine Eingaben und offenen Entwürfe bleiben erhalten.
        </p>
      </div>
    </div>
  );
}

/** Eine Angabe der Kontextzeile: Sinnbild, Beschriftung, Wert. */
function Kontext(props: {
  readonly sinnbild: ReactNode;
  readonly label: string;
  readonly wert: string;
}): ReactNode {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className="grid shrink-0 place-items-center text-text3">{props.sinnbild}</span>
      <div className="min-w-0">
        <div className="text-[10.5px] leading-[1.3] text-text3">{props.label}</div>
        <div className="truncate text-xs font-medium">{props.wert}</div>
      </div>
    </div>
  );
}

/**
 * Eine Taste des Ziffernblocks.
 *
 * Der zugängliche Name kommt aus `label` und nicht aus dem Inhalt: Bei der
 * Löschtaste ist der Inhalt ein Sinnbild, und «» wäre kein Name.
 */
function Taste(props: {
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled: boolean;
  readonly inhalt?: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={props.label}
      disabled={props.disabled}
      onClick={props.onClick}
      className="grid h-11 place-items-center rounded-xl border border-strong bg-card font-zahl text-base font-medium hover:bg-secondary disabled:opacity-50"
    >
      {props.inhalt ?? props.label}
    </button>
  );
}

/**
 * Das Gerüst hinter dem Schleier — graue Balken, sonst nichts.
 *
 * **Es ist absichtlich keine Nachbildung einer bestimmten Seite.** Ein
 * erkennbares Abbild der Belegliste behauptete, dass dort gerade die
 * Belegliste offen ist, und wäre in jedem anderen Fall eine kleine Lüge. Was
 * es leisten muss, ist eines: sagen, dass da etwas ist, das weitergeht.
 *
 * `aria-hidden`, weil es nichts bedeutet.
 */
function Platzhaltergeruest(): ReactNode {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden p-6">
      <div className="mb-6 flex items-center gap-4">
        <div className="h-7 w-40 rounded-md bg-track" />
        <div className="ml-auto h-7 w-24 rounded-md bg-track" />
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 h-2.5 w-20 rounded-full bg-track" />
            <div className="mb-3 h-5 w-28 rounded-md bg-track" />
            <div className="h-1 rounded-full bg-track" />
          </div>
        ))}
      </div>
      <div className="mt-4 h-[300px] rounded-2xl border border-border bg-card" />
    </div>
  );
}

/**
 * Die Initialen des Anzeigenamens, höchstens zwei.
 *
 * Kein Rückfall auf die E-Mail-Adresse: `display_name` ist in `app_user`
 * `NOT NULL`, und ein leerer Kreis ist ehrlicher als ein Buchstabe aus einer
 * Adresse, die niemand als Namen gemeint hat.
 */
function initialen(name: string): string {
  return name
    .split(/\s+/)
    .filter((teil) => teil !== '')
    .slice(0, 2)
    .map((teil) => teil.charAt(0).toUpperCase())
    .join('');
}

/**
 * Die Uhrzeit des Sperrzeitpunkts, in der Zeitzone des Anwenders.
 *
 * Nur die Uhrzeit und nicht das Datum: Die absolute Lebensdauer der Sitzung
 * beträgt zwölf Stunden, ein Sperrzeitpunkt liegt damit nie mehr als einen
 * halben Tag zurück. «11:42 Uhr» beantwortet die Frage; «10.09.2026, 11:42
 * Uhr» beantwortet sie auch, aber breiter.
 */
function uhrzeit(iso: string | undefined): string {
  if (iso === undefined) return 'Unbekannt';
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return 'Unbekannt';
  return `${zeitpunkt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
}
